/**
 * Batch file upload hook with concurrent processing.
 *
 * Manages a queue of files with independent lifecycles, processing up to
 * CONCURRENCY files simultaneously. Each file flows through:
 *   queued -> hashing -> uploading -> completed | failed | cancelled | duplicate
 *
 * Uses useReducer for atomic state transitions across N items.
 */

import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';

import { createPool } from './concurrency';
import {
  batchReducer,
  INITIAL_STATE,
  TERMINAL_STATUSES,
} from './batchUploadReducer';
import { processFile } from './batchUploadProcessor';
import type { BatchStats, UploadItem, UseBatchUploadReturn } from './types';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Max concurrent file upload pipelines (hash + upload + confirm). */
const CONCURRENCY = 5;

/** Auto-hide panel after all items are terminal for this many ms. */
const AUTO_HIDE_DELAY_MS = 8_000;

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useBatchUpload(): UseBatchUploadReturn {
  const [state, dispatch] = useReducer(batchReducer, INITIAL_STATE);
  const poolRef = useRef(createPool(CONCURRENCY));
  const autoHideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());

  // --- Derived stats (memoized) ---
  const stats = useMemo<BatchStats>(() => {
    const items = state.items;
    const total = items.length;
    const completed = items.filter((i) => i.status === 'completed').length;
    const failed = items.filter((i) => i.status === 'failed').length;
    const duplicates = items.filter((i) => i.status === 'duplicate').length;
    const cancelled = items.filter((i) => i.status === 'cancelled').length;
    const inProgress = items.filter(
      (i) => i.status === 'hashing' || i.status === 'uploading',
    ).length;
    const queued = items.filter((i) => i.status === 'queued').length;

    // Weighted progress: terminal items count as 100%, in-progress use their progress
    const terminalCount = completed + failed + duplicates + cancelled;
    const progressSum =
      terminalCount * 100 +
      items
        .filter((i) => i.status === 'uploading')
        .reduce((sum, i) => sum + i.progress, 0) +
      items.filter((i) => i.status === 'hashing').reduce((sum) => sum + 5, 0);
    const overallProgress = total > 0 ? Math.round(progressSum / total) : 0;

    return { total, completed, failed, duplicates, inProgress, queued, overallProgress };
  }, [state.items]);

  const isActive = stats.inProgress > 0 || stats.queued > 0;

  // --- Auto-hide ONLY on a clean, all-succeeded batch ---
  // If anything was deduped (already in library), failed, or cancelled, the
  // panel stays open until the user dismisses it — so the outcome is never
  // silently buried (the AW-43 "only 2 ingesting" confusion).
  const allSucceeded = stats.total > 0 && stats.completed === stats.total;

  useEffect(() => {
    if (autoHideTimerRef.current) {
      clearTimeout(autoHideTimerRef.current);
      autoHideTimerRef.current = null;
    }

    if (allSucceeded) {
      autoHideTimerRef.current = setTimeout(() => {
        dispatch({ type: 'HIDE_PANEL' });
      }, AUTO_HIDE_DELAY_MS);
    }

    return () => {
      if (autoHideTimerRef.current) {
        clearTimeout(autoHideTimerRef.current);
      }
    };
  }, [allSucceeded]);

  // --- Warn before refresh/navigation while uploads are in flight ---
  // A browser can't re-read a File after a reload (security), so an in-flight
  // upload cannot be auto-resumed — warning before loss is the correct guard.
  // Already-confirmed files are safe (their rows exist); only in-flight work
  // would be lost, and re-dropping it just re-creates duplicates.
  useEffect(() => {
    if (!isActive) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isActive]);

  // --- Process wrapper that closes over dispatch ---
  const processFileWithDispatch = useCallback(
    async (item: UploadItem, docClass?: string) => {
      await processFile(item, docClass, dispatch);
    },
    [],
  );

  // --- Add files to the queue ---
  const addFiles = useCallback(
    (files: File[], docClass?: string) => {
      if (files.length === 0) return;

      // ONE batch id per upload action: all files of this drop/selection share
      // it, so the backend records that they arrived together (641ef2e8).
      const uploadBatchId = crypto.randomUUID();
      const newItems: UploadItem[] = files.map((file) => ({
        id: crypto.randomUUID(),
        uploadBatchId,
        file,
        status: 'queued' as const,
        progress: 0,
        error: null,
        result: null,
        abortController: new AbortController(),
        docClass,
      }));

      // Register abort controllers in ref map (immune to stale closures)
      for (const item of newItems) {
        abortControllersRef.current.set(item.id, item.abortController);
      }

      dispatch({ type: 'ADD_FILES', items: newItems });

      // Submit each to the concurrency pool
      for (const item of newItems) {
        poolRef.current.add(() => processFileWithDispatch(item, docClass));
      }
    },
    [processFileWithDispatch],
  );

  // --- Cancel a single item (abort via ref map -- immune to stale closures) ---
  const cancelItem = useCallback((itemId: string) => {
    abortControllersRef.current.get(itemId)?.abort();
    abortControllersRef.current.delete(itemId);
    dispatch({ type: 'CANCEL_ITEM', id: itemId });
  }, []);

  // --- Cancel all (abort via ref map) ---
  const cancelAll = useCallback(() => {
    for (const controller of abortControllersRef.current.values()) {
      controller.abort();
    }
    abortControllersRef.current.clear();
    dispatch({ type: 'CANCEL_ALL' });
  }, []);

  // --- Remove a terminal item ---
  const removeItem = useCallback((itemId: string) => {
    abortControllersRef.current.delete(itemId);
    dispatch({ type: 'REMOVE_ITEM', id: itemId });
  }, []);

  // --- Clear all terminal items ---
  const clearCompleted = useCallback(() => {
    // Clean up abort controllers for terminal items
    for (const item of state.items) {
      if (TERMINAL_STATUSES.has(item.status)) {
        abortControllersRef.current.delete(item.id);
      }
    }
    dispatch({ type: 'CLEAR_COMPLETED' });
  }, [state.items]);

  // --- Retry a failed item (in-place, preserves list order) ---
  const retryItem = useCallback(
    (itemId: string) => {
      const item = state.items.find((i) => i.id === itemId);
      if (!item || item.status !== 'failed') return;

      const newController = new AbortController();
      abortControllersRef.current.set(itemId, newController);
      dispatch({ type: 'RETRY_ITEM', id: itemId, abortController: newController });

      // Build a fresh reference for the pool task
      const retryRef: UploadItem = { ...item, abortController: newController };
      poolRef.current.add(() => processFileWithDispatch(retryRef, retryRef.docClass));
    },
    [state.items, processFileWithDispatch],
  );

  // --- Panel controls ---
  const toggleMinimize = useCallback(() => {
    dispatch({ type: 'TOGGLE_MINIMIZE' });
  }, []);

  const hidePanel = useCallback(() => {
    dispatch({ type: 'HIDE_PANEL' });
  }, []);

  return {
    items: state.items,
    isActive,
    isPanelVisible: state.isPanelVisible,
    isPanelMinimized: state.isPanelMinimized,
    stats,
    addFiles,
    cancelItem,
    cancelAll,
    removeItem,
    clearCompleted,
    retryItem,
    toggleMinimize,
    hidePanel,
  };
}
